/**
 * Bitmap operations for a location plan: turning it, and trimming the white
 * margins off it. Both work on the STORED image rather than keeping a flag
 * beside it, so neither surface needs a rotated or cropped draw path -- the
 * editor and the PDF go on fitting an upright, tight image into the same box.
 */

const loadImage = (source: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new window.Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('The plan image could not be read')); };
    img.src = url;
  });

const encodeJpeg = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not encode the plan'))), 'image/jpeg', quality);
  });

export interface BitmapResult {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Rotate by a multiple of 90 degrees, clockwise. A landscape drawing sheet
 * fits the portrait appendix page only when it is stood on its side, which is
 * how the firm's Word reports have always shown it.
 */
export const rotateBitmap = async (
  source: Blob,
  degrees: 90 | 180 | 270,
  quality = 0.92
): Promise<BitmapResult> => {
  const image = await loadImage(source);
  const swap = degrees !== 180;
  const canvas = window.document.createElement('canvas');
  canvas.width = swap ? image.naturalHeight : image.naturalWidth;
  canvas.height = swap ? image.naturalWidth : image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not draw the plan');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);
  return { blob: await encodeJpeg(canvas, quality), width: canvas.width, height: canvas.height };
};

/** The window kept by trimBitmap, in pixels of the untrimmed image. */
export interface TrimCrop {
  x: number;
  y: number;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
}

export interface TrimResult extends BitmapResult {
  /** Null when nothing was trimmed (no margins, or nothing recognisable as content). */
  crop: TrimCrop | null;
}

/**
 * Trim the white margins so the drawing's own extents are what gets fitted
 * to the page. A rasterised PDF sheet carries its paper margins and often a
 * border's worth of white beyond the title block; fitted as-is, the plan
 * itself uses a fraction of the available box.
 *
 * A pixel is content when any channel is darker than `threshold` (JPEG
 * noise around white stays above it). The tight bounds are padded by
 * `padFraction` of the longer side so the drawing does not touch the box
 * edge. Nothing is trimmed when the content is the whole image already or
 * when almost nothing registers as content (a blank or near-blank upload),
 * in which case `crop` is null and the blob is returned unchanged.
 */
export const trimBitmap = async (
  source: Blob,
  options: { threshold?: number; padFraction?: number; quality?: number } = {}
): Promise<TrimResult> => {
  const { threshold = 235, padFraction = 0.012, quality = 0.92 } = options;
  const image = await loadImage(source);
  const w = image.naturalWidth;
  const h = image.naturalHeight;

  const canvas = window.document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not draw the plan');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(image, 0, 0);
  const { data } = ctx.getImageData(0, 0, w, h);

  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    const rowStart = y * w * 4;
    for (let x = 0; x < w; x++) {
      const i = rowStart + x * 4;
      if (data[i] < threshold || data[i + 1] < threshold || data[i + 2] < threshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  const untouched: TrimResult = { blob: source, width: w, height: h, crop: null };
  if (maxX < 0) return untouched;

  const pad = Math.max(8, Math.round(Math.max(w, h) * padFraction));
  const x0 = Math.max(0, minX - pad);
  const y0 = Math.max(0, minY - pad);
  const x1 = Math.min(w, maxX + 1 + pad);
  const y1 = Math.min(h, maxY + 1 + pad);
  const cw = x1 - x0;
  const ch = y1 - y0;
  // Nothing worth doing: the content already fills the image, or it is a
  // speck on a blank sheet. "Fills" allows 0.5% of the long side: the padding
  // scales with the image and JPEG ringing moves the edges a pixel or two, so
  // a plan trimmed once came out a few pixels smaller on every further Trim
  // instead of reporting nothing to trim.
  const tolerance = Math.max(2, Math.round(Math.max(w, h) * 0.005));
  if (cw >= w - tolerance && ch >= h - tolerance) return untouched;
  if (cw * ch < w * h * 0.02) return untouched;

  const out = window.document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const octx = out.getContext('2d');
  if (!octx) throw new Error('Could not draw the plan');
  octx.drawImage(canvas, x0, y0, cw, ch, 0, 0, cw, ch);
  return {
    blob: await encodeJpeg(out, quality),
    width: cw,
    height: ch,
    crop: { x: x0, y: y0, width: cw, height: ch, sourceWidth: w, sourceHeight: h },
  };
};

/**
 * Where a marker placed on the untrimmed image sits on the trimmed one:
 * the same pixel, expressed as a fraction of the new bounds. Clamped, so a
 * pin that was out in the margin lands on the nearest edge.
 */
export const remapForCrop = (x: number, y: number, crop: TrimCrop): { x: number; y: number } => ({
  x: Math.min(1, Math.max(0, (x * crop.sourceWidth - crop.x) / crop.width)),
  y: Math.min(1, Math.max(0, (y * crop.sourceHeight - crop.y) / crop.height)),
});
